import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { BankCardIcon } from "@/shared/icons";
import Button from "@/shared/ui/button/Button";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import { improvementsIconsList } from "@/domains/business/helpers/improvementsIconsList";
import useGetFrozenImprovements from "@/domains/business/hooks/useGetFrozenImprovements";
import ImprovementsListItem from "@/domains/business/components/improvements/ImprovementsListItem";

type Props = {
  businessId: number;
  device: DeviceEnum;
};

export default function BusinessImprovementsPreviousContainer({ businessId, device }: Props) {
  const { t } = useTranslation("business");

  const {
    isLoading,
    improvementsInfo,
    isAcceptLoading,
    acceptImprovements,
    isDeclineLoading,
    declineImprovements,
  } = useGetFrozenImprovements(businessId, device);

  const header = t("business.improvements.prevImprovements.title");
  const footer = (
    <div className="flex flex-col gap-5">
      <div>
        <p className="mb-1 text-20 font-bold text-accent-solid">
          {formatPrice(improvementsInfo?.commonPrice)} ₽
        </p>
        <p className="text-15 font-medium text-white/50">
          {t("business.improvements.prevImprovements.priceDescription")}
        </p>
      </div>
      <div className="flex gap-2">
        <Button className="grow gap-2.5 h-11" onClick={acceptImprovements}>
          <BankCardIcon className="w-4.5" />
          {t("business.improvements.prevImprovements.accept")}
        </Button>
        <Button variant="solid-surface" className="grow h-11" onClick={declineImprovements}>
          {t("business.improvements.prevImprovements.decline")}
        </Button>
      </div>
    </div>
  );

  return (
    <div
      className="absolute z-2 top-0 left-0 flex size-full bg-dark-900/84"
      style={{ backdropFilter: "blur(2px)" }}
    >
      <DeviceTabContentWrapper
        header={header}
        footer={footer}
        onClose={declineImprovements}
        isLoading={isLoading || isAcceptLoading || isDeclineLoading}
        className="grow-0 w-[33.5rem] h-full ml-auto bg-dark-900 shadow-[0px_0px_200px_0px_rgba(1,100,172,0.6)]"
      >
        <p className="relative py-5 pl-1 text-16 font-medium">
          <span className="absolute top-0 left-0 flex w-full h-px bg-[#191919]">
            <span className="flex w-[12.125rem] h-px bg-accent-solid" />
          </span>
          {t("business.improvements.prevImprovements.description")}
        </p>
        <div className="flex justify-between mb-3.5">
          <p className="text-15 font-medium text-white/50">
            {t("business.improvements.list.improvements")}:
          </p>
          <p className="text-15 font-medium text-white/50">
            {t("business.improvements.list.currentLevel")}:
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          {improvementsInfo &&
            improvementsInfo.improvements.map((item) => {
              const Icon = improvementsIconsList[item.type.toLowerCase()];
              return (
                <ImprovementsListItem
                  key={item.id}
                  text={t(`business.improvements.list.${item.type.toLowerCase()}`)}
                  currentLevel={item.currentLevel}
                  maxLevel={item.maxLevel}
                  icon={<Icon className="size-4" />}
                  tooltipDescription={t(
                    `business.improvements.${item.type.toLowerCase()}.description`,
                  )}
                />
              );
            })}
        </div>
      </DeviceTabContentWrapper>
    </div>
  );
}
